import { impersonationRef } from "@client/impersonationRef";
import { trpcVanilla } from "@client/trpc";
import { type StorageLike } from "@client/sync";
import { watchProgressThrottleMs } from "@lib/videoStateTypes";
import { type MobileVideoStore } from "../mobileVideoStore";
import { WatchProgressThrottle } from "../watchProgressThrottle";
import { type BookmarkPayload, createBookmarkSync } from "./bookmarkSync";
import { createPlaylistSync, type PlaylistPayload } from "./playlistSync";
import { createProgressSync, type ProgressPayload } from "./progressSync";

// In-memory StorageLike for the web outbox: lives only for the page session, so
// a persisted queue never carries writes across a real reload.
function createMemoryStorage(): StorageLike {
  const map = new Map<string, string>();

  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

// Web outboxes, keyed by the real (underlying) user id. Module-level so they
// survive QueueProvider remounts (a round/impersonation switch must not drop
// pending writes), but isolated per user so one user's queued writes can never
// flush under another user's session — independent of sign-out/reload timing.
const webOutboxes = new Map<string, StorageLike>();

export function getWebOutbox(userKey: string): StorageLike {
  let storage = webOutboxes.get(userKey);

  if (storage == null) {
    storage = createMemoryStorage();
    webOutboxes.set(userKey, storage);
  }

  return storage;
}

// Fetches the user's video state to hydrate the store. The web funder workspace
// passes one that queries the server AND opportunistically seeds
// VideoStatesProvider's React Query cache when it's empty — so the player's hook
// reads that entry on the initial load instead of firing a duplicate request —
// WITHOUT ever overwriting rows the player already holds (its optimistic /
// newer-timestamped progress must win; the queue never clobbers it). The native
// path omits this and falls back to a direct vanilla query, having no
// VideoStatesProvider to dedupe against.
export type UserVideoStateFetcher = () => Promise<
  Awaited<ReturnType<typeof trpcVanilla.getUserVideoState.query>>
>;

export interface VideoSyncController {
  start: () => void;
  stop: () => void;
  reportWatchProgress: (videoId: string) => void;
  flushWatchProgress: (videoId: string) => void;
  hydrateFromServer: (roundId?: string) => Promise<void>;
}

// Pull the latest server state and reconcile it into the store via LWW.
// Best-effort: a transient failure is swallowed so callers can fire-and-forget.
// Exposed standalone so one-shot consumers (e.g. opening the Queue drawer) can
// refresh without holding a sync controller. `fetcher` (see
// UserVideoStateFetcher) lets the web path share the player's cache; otherwise
// it falls back to a direct vanilla query.
export async function hydrateUserVideoState(
  store: MobileVideoStore,
  roundId?: string,
  fetcher?: UserVideoStateFetcher,
): Promise<void> {
  try {
    const server = fetcher
      ? await fetcher()
      : await trpcVanilla.getUserVideoState.query({ roundId });
    store.applyServerHydration(server);
  } catch (err) {
    console.warn("[videoSync] hydrateFromServer failed", err);
  }
}

// Binds the outbound sync queues + hydration + watch-progress throttle to a
// specific store instance. The iPhone runtime creates one bound to its
// long-lived singleton; the web QueueProvider creates one per in-memory
// instance.
export function createVideoSync(
  store: MobileVideoStore,
  opts: {
    persistOutbox?: boolean;
    outboxKey?: string;
    fetchUserVideoState?: UserVideoStateFetcher;
  } = {},
): VideoSyncController {
  // Native (offline-first) persists the outbox to localStorage so writes survive
  // app restarts. Web passes persistOutbox: false and uses a module-level
  // in-memory outbox keyed by the real user (outboxKey): it survives
  // QueueProvider remounts (so a round/impersonation switch doesn't drop pending
  // writes) yet never persists across a reload or flushes under another user.
  const storage: StorageLike | undefined =
    opts.persistOutbox === false
      ? getWebOutbox(opts.outboxKey ?? "anonymous")
      : undefined;

  // Commit gate for every feature: a send already in flight when the user
  // switches into impersonation goes out with acting-as headers, the server
  // no-ops it, and an ungated flush would dequeue the real user's write as
  // if delivered. Declining the commit keeps it queued for the next real
  // self session instead.
  const notImpersonating = () => impersonationRef.current == null;

  const progress = createProgressSync({
    onSuccess: (result) => {
      store.applyServerProgress(result.current);
    },
    storage,
    shouldCommit: notImpersonating,
  });

  // No-op onSuccess: local state is already authoritative for the write we
  // just sent; cross-device convergence is handled by hydration.
  const bookmark = createBookmarkSync({
    onSuccess: () => {},
    storage,
    shouldCommit: notImpersonating,
  });
  const playlist = createPlaylistSync({
    storage,
    shouldCommit: notImpersonating,
  });

  const throttle = new WatchProgressThrottle((videoId) => {
    store.enqueueWatchProgress(videoId);
  }, watchProgressThrottleMs);

  store.setSyncFeatures({
    enqueueProgress: (payload: ProgressPayload) => progress.enqueue(payload),
    enqueueBookmark: (payload: BookmarkPayload) => bookmark.enqueue(payload),
    enqueuePlaylist: (payload: PlaylistPayload) => playlist.enqueue(payload),
  });

  return {
    start: () => {
      progress.start();
      bookmark.start();
      playlist.start();
    },
    stop: () => {
      progress.stop();
      bookmark.stop();
      playlist.stop();
    },
    reportWatchProgress: (videoId: string) => throttle.notify(videoId),
    flushWatchProgress: (videoId: string) => throttle.flushNow(videoId),
    hydrateFromServer: (roundId?: string) => {
      return hydrateUserVideoState(store, roundId, opts.fetchUserVideoState);
    },
  };
}
